import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi as defaultAdminApi } from './admin-api';
import { generateTemporaryPassword } from '../lib/temporary-password';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { ApiError } from '../api/api-error';
import { PageState } from '../components/PageState';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate, formatMoney, formatPaymentDate, PAYMENT_METHOD_LABELS, PAYMENT_STATUS_LABELS } from '../lib/format';
import { createPendingPaymentStore, getDefaultLocalStorage } from '../lib/pending-payment';
import { normalizeDocumentNumber, formatDocumentNumber, isValidDocumentNumber } from '../lib/document-number';
import { isValidPhone } from '../lib/phone';
import { MembershipForm } from './MembershipForm';
import { MembershipActions } from './MembershipActions';
import { ResetPasswordForm } from './ResetPasswordForm';
import { TemporaryPasswordNotice } from './TemporaryPasswordNotice';
import { PaymentForm } from './PaymentForm';
import { createTrainingApi } from '../training/training-api';
import { RoutineSection } from './routine/RoutineSection';
import { MemberProgressSection } from './routine/MemberProgressSection';
import { ConfirmationSheet } from '../components/ConfirmationSheet';
import { useFocusTrap } from '../components/useFocusTrap';
import './admin.css';

// Module-level singleton (same convention as `defaultAdminApi`): a stable
// reference across every render, so `trainingApi` never becomes a new object
// on each parent re-render — RoutineSection/MemberProgressSection depend on
// it in a `useEffect`, and a fresh function identity every render would
// refetch on every keystroke elsewhere on the page.
const defaultTrainingApi = createTrainingApi();

const TITLE = 'Detalle del socio';
const TABS = [
  { id: 'cuota', label: 'Cuota' },
  { id: 'rutina', label: 'Rutina' },
  { id: 'progreso', label: 'Progreso' },
];
const DAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toProfileForm(detail) {
  const detailUser = detail && detail.user;
  const profile = detail && detail.profile;
  return {
    username: (detailUser && detailUser.username) || '',
    email: (detailUser && detailUser.email) || '',
    fitnessGoal: (profile && profile.fitnessGoal) || '',
    targetDaysPerWeek: profile && profile.targetDaysPerWeek != null ? String(profile.targetDaysPerWeek) : '',
    documentNumber: (profile && profile.documentNumber) || '',
    phone: (profile && profile.phone) || '',
    medicalNotes: (profile && profile.medicalNotes) || '',
    coachUserId: (profile && profile.coachUserId) || '',
  };
}

/**
 * `/admin/socios/:id` (spec "Administrador": "El detalle consume /members/:id
 * y agrupa: identidad y perfil editable; activación/desactivación con
 * confirmación; períodos ordenados del más reciente al más antiguo; pagos de
 * cada período y saldo expuesto por la API; creación del próximo período a
 * partir de un plan activo"). Cobro parcial/total is Task 10's own work — this
 * page shows each period's existing payments/balance but never registers one.
 *
 * `GET /members/:id` and `GET /plans` are fetched together on mount and again
 * after EVERY successful mutation (profile edit, activate/deactivate, new
 * period, password reset) — never patched locally — so balances, statuses and
 * `relevantMembershipId` always come straight from the server (spec: "el
 * cliente nunca recalcula saldos, estados ni fechas de período").
 *
 * Just a `useParams()` shim over the real page, keyed by `id` (fix round 1,
 * item 3): React Router keeps the same component instance mounted when only
 * the `:id` param changes, so without a fresh `key` every local UI state
 * (an open confirmation, an open form, a just-shown temporary password)
 * would leak from one member's page onto the next one navigated to.
 */
export function MemberDetailPage(props) {
  const { id } = useParams();
  return <MemberDetailPageForId key={id} id={id} {...props} />;
}

function MemberDetailPageForId({
  id,
  api = defaultAdminApi,
  generatePassword = generateTemporaryPassword,
  paymentStorage = getDefaultLocalStorage(),
  trainingApi = defaultTrainingApi,
}) {
  const { handleApiError, user } = useAuth();
  const [activeTab, setActiveTab] = useState('cuota');
  // Fix round 1, item 1: a period whose balance just reached 0 must NOT hide
  // an unresolved pending payment (e.g. the response to a full payment was
  // lost, then the admin reloaded) — the store, not `balance`, is what says
  // whether a charge is still unresolved. Read from the exact same `storage`
  // passed down to every `PaymentForm` below (never each one's own default),
  // so this check and what those components read/write always agree.
  const paymentStore = useState(() => createPendingPaymentStore(paymentStorage))[0];
  const [state, setState] = useState({
    status: 'loading', detail: null, plans: null, coaches: [], message: '',
  });
  const [reloadError, setReloadError] = useState('');
  const [profileForm, setProfileForm] = useState({
    username: '', email: '', fitnessGoal: '', targetDaysPerWeek: '', documentNumber: '', phone: '', medicalNotes: '', coachUserId: '',
  });
  const [profileError, setProfileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [profileNotice, setProfileNotice] = useState('');
  const [profileSubmitting, setProfileSubmitting] = useState(false);

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [communitySubmitting, setCommunitySubmitting] = useState(false);

  const [membershipFormOpen, setMembershipFormOpen] = useState(false);
  const [periodNotice, setPeriodNotice] = useState('');
  const [resetFormOpen, setResetFormOpen] = useState(false);
  const membershipSheetRef = useFocusTrap(membershipFormOpen);
  const resetSheetRef = useFocusTrap(resetFormOpen);
  const [resetPassword, setResetPassword] = useState(null);

  const clearBusinessData = useCallback(
    () => {
      setState({
        status: 'loading', detail: null, plans: null, coaches: [], message: '',
      });
      setReloadError('');
    },
    [],
  );

  const load = useCallback(() => {
    let active = true;
    setState({
      status: 'loading', detail: null, plans: null, message: '',
    });

    Promise.all([api.getMember(id), api.listPlans(), api.listCoaches ? api.listCoaches() : Promise.resolve({ coaches: [] })])
      .then(([detail, plansResponse, coachesResponse]) => {
        if (!active) return;
        setState({
          status: 'ready', detail, plans: (plansResponse && plansResponse.plans) || [], coaches: ((coachesResponse && coachesResponse.coaches) || []).filter(coach => coach.isActive || coach.id === detail.profile?.coachUserId), message: '',
        });
        setProfileForm(toProfileForm(detail));
      })
      .catch((error) => {
        if (!active) return;
        const outcome = handleApiError(error, { clearBusinessData });
        if (outcome.handled) return;
        setState({
          status: 'error', detail: null, plans: null, coaches: [], message: errorMessageFor(error),
        });
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id, handleApiError]);

  useEffect(() => load(), [load]);

  // Used after every mutation: re-fetches detail+plans WITHOUT flipping
  // `status` back to 'loading' first, so the page (and any open form) doesn't
  // flash away — only the data it shows swaps in once the fresh reads
  // resolve.
  //
  // Final whole-phase review (I3): a failure HERE follows a mutation that
  // already succeeded server-side (a reset password, a new period, a
  // confirmed payment) — the old behavior fell back to the full-page error
  // state, which unmounted everything including the just-shown one-time
  // temporary password and any payment banner. Money/secret-critical, so a
  // background reload failure instead keeps `detail`/`plans` exactly as they
  // were and only surfaces a small inline notice with its own retry, never
  // touching `state`. Only the FIRST load (`load` above) still uses the
  // full-page error state — there is nothing yet to preserve at that point.
  const reload = useCallback(() => Promise.all([api.getMember(id), api.listPlans(), api.listCoaches ? api.listCoaches() : Promise.resolve({ coaches: [] })])
    .then(([detail, plansResponse, coachesResponse]) => {
      setState({
        status: 'ready', detail, plans: (plansResponse && plansResponse.plans) || [], coaches: ((coachesResponse && coachesResponse.coaches) || []).filter(coach => coach.isActive || coach.id === detail.profile?.coachUserId), message: '',
      });
      setProfileForm(toProfileForm(detail));
      setReloadError('');
    })
    .catch((error) => {
      const outcome = handleApiError(error, { clearBusinessData });
      if (outcome.handled) return;
      setReloadError(errorMessageFor(error));
    }), [api, id, handleApiError, clearBusinessData]);

  async function handleProfileSubmit(event) {
    event.preventDefault();
    if (profileSubmitting) return;
    setProfileError('');
    setEmailError('');
    setDocumentError('');
    setProfileNotice('');

    const trimmedUsername = profileForm.username.trim();
    const trimmedEmail = profileForm.email.trim();
    const trimmedPhone = profileForm.phone.trim();
    if (!trimmedUsername) {
      setProfileError('Ingresá un nombre para el socio.');
      return;
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setProfileError('Ingresá un email válido.');
      return;
    }
    if (!isValidDocumentNumber(profileForm.documentNumber)) {
      setProfileError('El DNI debe tener entre 6 y 10 dígitos.');
      return;
    }
    if (!isValidPhone(trimmedPhone)) {
      setProfileError('Ingresá un teléfono válido (dígitos, espacios, +, -, ( y )).');
      return;
    }

    const payload = {
      username: trimmedUsername,
      email: trimmedEmail,
      fitnessGoal: profileForm.fitnessGoal.trim() === '' ? null : profileForm.fitnessGoal.trim(),
      targetDaysPerWeek: profileForm.targetDaysPerWeek === '' ? null : Number(profileForm.targetDaysPerWeek),
      documentNumber: profileForm.documentNumber || null,
      phone: trimmedPhone || null,
      medicalNotes: profileForm.medicalNotes.trim() || null,
      coachUserId: profileForm.coachUserId || null,
    };
    const assignedCoachId = state.detail.profile?.coachUserId || null;
    const selectedCoachId = profileForm.coachUserId || null;
    if (selectedCoachId === assignedCoachId && selectedCoachId && !state.coaches.some(coach => coach.id === selectedCoachId && coach.isActive)) {
      delete payload.coachUserId;
    }

    setProfileSubmitting(true);
    try {
      await api.updateMember(id, payload);
      await reload();
      setProfileNotice('Guardamos los cambios.');
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData });
      if (outcome.handled) return;
      if (caught instanceof ApiError && caught.code === 'email_taken') {
        setEmailError(errorMessageFor(caught));
      } else if (caught instanceof ApiError && caught.code === 'document_taken') {
        setDocumentError(errorMessageFor(caught));
      } else {
        setProfileError(errorMessageFor(caught));
      }
    } finally {
      setProfileSubmitting(false);
    }
  }

  function askDeactivate() {
    setConfirmDeactivate(true);
    setStatusError('');
  }

  function cancelDeactivate() {
    setConfirmDeactivate(false);
  }

  async function setActive(isActive) {
    setStatusError('');
    setStatusSubmitting(true);
    try {
      await api.updateMember(id, { isActive });
      setConfirmDeactivate(false);
      await reload();
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData });
      if (!outcome.handled) setStatusError(errorMessageFor(caught));
    } finally {
      setStatusSubmitting(false);
    }
  }

  async function toggleCommunitySuspension() {
    setStatusError('');
    setCommunitySubmitting(true);
    try {
      await api.updateMember(id, { communitySuspended: !detailUser.communitySuspended });
      await reload();
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData });
      if (!outcome.handled) setStatusError(errorMessageFor(caught));
    } finally {
      setCommunitySubmitting(false);
    }
  }

  function handleMembershipCreated() {
    setMembershipFormOpen(false);
    setPeriodNotice('');
    reload();
  }

  function handlePeriodChanged(message) {
    setPeriodNotice(message || '');
    reload();
  }

  function handleReset(password) {
    setResetFormOpen(false);
    setResetPassword(typeof password === 'string' ? { password } : password);
    reload();
  }

  if (state.status === 'loading') return <PageState title={TITLE} status="loading" />;
  if (state.status === 'error') {
    return (
      <PageState
        title={TITLE}
        status="error"
        message={state.message}
        action={<Link to="/admin/socios" className="gym-button gym-button--secondary">Volver a socios</Link>}
      />
    );
  }

  const { detail, plans } = state;
  const detailUser = detail.user;
  const memberships = detail.memberships || [];

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-admin-detail__header">
        <div>
          <p className="gym-admin-detail__back"><Link to="/admin/socios">← Volver a socios</Link></p>
          <h1 id="gym-page-title" className="gym-page__title">{detailUser.username}</h1>
          <p className="gym-admin-detail__email">{detailUser.email}</p>
        </div>
        <span className={detailUser.isActive ? 'gym-badge gym-badge--success' : 'gym-badge gym-badge--neutral'}>
          {detailUser.isActive ? 'Activo' : 'Inactivo'}
        </span>
      </header>

      {resetPassword && (
        <TemporaryPasswordNotice
          username={detailUser.username}
          password={resetPassword.password}
          passwordIsDocument={resetPassword.passwordIsDocument}
          onClose={() => setResetPassword(null)}
        />
      )}

      {/* Final whole-phase review (I3): a mutation (reset, new period,
          payment) already succeeded server-side by the time this can appear
          — only the FOLLOW-UP read failed. Non-blocking on purpose: it never
          replaces the page (which would drop the notice above, an open form,
          or a payment's pending banner), only offers to retry the read. */}
      {reloadError && (
        <div role="alert" className="gym-form__error gym-admin-detail-reload-error">
          <span>{reloadError}</span>
          <button type="button" className="gym-admin-detail-reload-error__retry" onClick={reload}>Reintentar</button>
        </div>
      )}

      {confirmDeactivate && (
        <ConfirmationSheet
          title="¿Desactivar a este socio?"
          confirmLabel="Sí, desactivar"
          pendingLabel="Desactivando…"
          busy={statusSubmitting}
          onConfirm={() => setActive(false)}
          onClose={cancelDeactivate}
          error={statusError}
        />
      )}

      {/* Task 7 (rutina + progreso, additive): three tabs over the existing
          "Cuota" content (Perfil/Acciones/Períodos, untouched below) plus the
          two new sections. Each non-active panel is unmounted rather than
          hidden with CSS, so switching tabs always re-fetches fresh data
          instead of showing a stale routine/progress snapshot — consistent
          with this page's own rule that a mutation is always followed by a
          server re-read, never a local patch. */}
      <div className="gym-admin-detail-tabs" role="tablist" aria-label="Secciones del socio">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`gym-admin-detail-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`gym-admin-detail-panel-${tab.id}`}
            className={`gym-admin-detail-tab${activeTab === tab.id ? ' gym-admin-detail-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'rutina' && (
        <div id="gym-admin-detail-panel-rutina" role="tabpanel" aria-labelledby="gym-admin-detail-tab-rutina">
          <RoutineSection memberId={id} api={trainingApi} />
        </div>
      )}

      {activeTab === 'progreso' && (
        <div id="gym-admin-detail-panel-progreso" role="tabpanel" aria-labelledby="gym-admin-detail-tab-progreso">
          <MemberProgressSection memberId={id} api={trainingApi} />
        </div>
      )}

      {/* Two columns from ~1100px up (Perfil + Acciones left, Períodos
          right — a live-preview finding: the single stacked column left
          most of a desktop screen's width unused and put the periods table
          below the fold). Below that width everything stacks in one column,
          same as every other page in this tree. */}
      {activeTab === 'cuota' && (
      <div id="gym-admin-detail-panel-cuota" role="tabpanel" aria-labelledby="gym-admin-detail-tab-cuota" className="gym-admin-detail-layout">
        <div className="gym-admin-detail-layout__main">
          <div className="gym-card">
            <h2 className="gym-card__title">Perfil</h2>
            <form className="gym-form" onSubmit={handleProfileSubmit} noValidate aria-label="Editar perfil">
              {profileError && <p role="alert" className="gym-form__error">{profileError}</p>}
              {profileNotice && <p role="status" className="gym-form__notice">{profileNotice}</p>}

              <div className="gym-field">
                <label htmlFor="detail-username" className="gym-field__label">Nombre</label>
                <input
                  id="detail-username"
                  name="username"
                  type="text"
                  required
                  value={profileForm.username}
                  disabled={profileSubmitting}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, username: event.target.value }))}
                  className="gym-input"
                />
              </div>

              <div className="gym-field">
                <label htmlFor="detail-email" className="gym-field__label">Email</label>
                <input
                  id="detail-email"
                  name="email"
                  type="email"
                  required
                  value={profileForm.email}
                  disabled={profileSubmitting}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, email: event.target.value }))}
                  aria-describedby={emailError ? 'detail-email-error' : undefined}
                  className="gym-input"
                />
                {emailError && <p id="detail-email-error" role="alert" className="gym-admin-field-error">{emailError}</p>}
              </div>

              <div className="gym-field">
                <label htmlFor="detail-goal" className="gym-field__label">Objetivo (opcional)</label>
                <input
                  id="detail-goal"
                  name="fitnessGoal"
                  type="text"
                  value={profileForm.fitnessGoal}
                  disabled={profileSubmitting}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, fitnessGoal: event.target.value }))}
                  className="gym-input"
                />
              </div>

              <div className="gym-field">
                <label htmlFor="detail-days" className="gym-field__label">Días por semana (opcional)</label>
                <select
                  id="detail-days"
                  name="targetDaysPerWeek"
                  value={profileForm.targetDaysPerWeek}
                  disabled={profileSubmitting}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, targetDaysPerWeek: event.target.value }))}
                  className="gym-input"
                >
                  <option value="">Sin definir</option>
                  {DAYS_OPTIONS.map((day) => <option key={day} value={day}>{day}</option>)}
                </select>
              </div>

              <div className="gym-field">
                <label htmlFor="detail-coach" className="gym-field__label">Profesor asignado (opcional)</label>
                <select id="detail-coach" name="coachUserId" value={profileForm.coachUserId} disabled={profileSubmitting} onChange={(event) => setProfileForm((prev) => ({ ...prev, coachUserId: event.target.value }))} className="gym-input">
                  <option value="">Sin profesor</option>
                  {state.coaches.map((coach) => <option key={coach.id} value={coach.id} disabled={!coach.isActive}>{coach.username}{coach.isActive ? '' : ' (inactivo)'}</option>)}
                </select>
              </div>

              <div className="gym-field">
                <label htmlFor="detail-document" className="gym-field__label">DNI (opcional)</label>
                <input
                  id="detail-document"
                  name="documentNumber"
                  type="text"
                  inputMode="numeric"
                  value={formatDocumentNumber(profileForm.documentNumber)}
                  disabled={profileSubmitting}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, documentNumber: normalizeDocumentNumber(event.target.value) }))}
                  aria-describedby={documentError ? 'detail-document-error' : undefined}
                  className="gym-input"
                />
                {documentError && <p id="detail-document-error" role="alert" className="gym-admin-field-error">{documentError}</p>}
              </div>

              <div className="gym-field">
                <label htmlFor="detail-phone" className="gym-field__label">Teléfono (opcional)</label>
                <input
                  id="detail-phone"
                  name="phone"
                  type="tel"
                  value={profileForm.phone}
                  disabled={profileSubmitting}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, phone: event.target.value }))}
                  className="gym-input"
                />
              </div>

              <div className="gym-field">
                <label htmlFor="detail-medical-notes" className="gym-field__label">Observaciones de salud / condición (opcional)</label>
                <textarea id="detail-medical-notes" name="medicalNotes" maxLength={1000} value={profileForm.medicalNotes} disabled={profileSubmitting} onChange={(event) => setProfileForm((prev) => ({ ...prev, medicalNotes: event.target.value }))} className="gym-input" />
              </div>

              <button type="submit" disabled={profileSubmitting} className="gym-button gym-button--primary">
                {profileSubmitting ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </form>
          </div>

          <div className="gym-card">
            <h2 className="gym-card__title">Acciones</h2>
            {statusError && !confirmDeactivate && <p role="alert" className="gym-form__error">{statusError}</p>}
            <div className="gym-admin-detail-actions">
              <button type="button" tabIndex={resetFormOpen ? -1 : undefined} className="gym-button gym-button--danger" onClick={() => setResetFormOpen(true)}>
                  Restablecer contraseña
              </button>
              <button type="button" className={`gym-button ${detailUser.communitySuspended ? 'gym-button--secondary' : 'gym-button--danger'}`} disabled={communitySubmitting} onClick={toggleCommunitySuspension}>
                {detailUser.communitySuspended ? 'Quitar suspensión de Comunidad' : 'Suspender en Comunidad'}
              </button>
              {detailUser.isActive ? (
                <button type="button" className="gym-button gym-button--danger" onClick={askDeactivate}>Desactivar</button>
              ) : (
                <button
                  type="button"
                  className="gym-button gym-button--success"
                  disabled={statusSubmitting}
                  onClick={() => setActive(true)}
                >
                  {statusSubmitting ? 'Activando…' : 'Activar'}
                </button>
              )}
            </div>
          </div>

          {resetFormOpen && (
            <div className="gym-admin-detail-sheet-backdrop">
              <section ref={resetSheetRef} tabIndex={-1} className="gym-admin-detail-sheet">
                <div className="gym-admin-detail-sheet__header">
                  <h2>Restablecer contraseña</h2>
                  <button type="button" className="gym-admin-detail-sheet__close" aria-label="Cerrar" onClick={() => setResetFormOpen(false)}>×</button>
                </div>
                <ResetPasswordForm
                  api={api}
                  memberId={id}
                  memberUsername={detailUser.username}
                  memberEmail={detailUser.email}
                  memberDocumentNumber={detail.profile?.documentNumber}
                  generatePassword={generatePassword}
                  onReset={handleReset}
                  onCancel={() => setResetFormOpen(false)}
                />
              </section>
            </div>
          )}
        </div>

        <div className="gym-admin-detail-layout__periods">
          <div className="gym-admin-detail-periods-header">
            <div className="gym-admin-detail-periods__heading">
              <h2 className="gym-admin-detail-periods__title">Períodos</h2>
              <p className="gym-admin-detail-periods__subtitle">Cuotas y pagos</p>
            </div>
            <button
              type="button"
              tabIndex={membershipFormOpen ? -1 : undefined}
              className="gym-button gym-button--primary"
              disabled={!detailUser.isActive}
              onClick={() => setMembershipFormOpen(true)}
            >
              Nuevo período
            </button>
          </div>
          {!detailUser.isActive && !membershipFormOpen && (
            <p className="gym-admin-field-hint">Reactivá al socio para poder abrir un período nuevo.</p>
          )}

          {membershipFormOpen && (
            <div className="gym-admin-detail-sheet-backdrop">
              <section ref={membershipSheetRef} tabIndex={-1} className="gym-admin-detail-sheet">
                <MembershipForm
                  api={api}
                  memberId={id}
                  plans={plans}
                  latestEndDate={memberships[0] ? memberships[0].endDate : ''}
                  memberships={memberships}
                  onCreated={handleMembershipCreated}
                  onCancel={() => setMembershipFormOpen(false)}
                />
              </section>
            </div>
          )}

          {periodNotice && <p role="status" className="gym-form__notice">{periodNotice}</p>}

          {memberships.length === 0 ? (
            <div className="gym-state gym-state--empty gym-admin-detail-periods__empty">
              <p className="gym-state__text">Este socio todavía no tiene cuotas ni pagos registrados. Asignale un plan con “Nuevo período” para empezar a cobrarle.</p>
            </div>
          ) : (
            // Live-preview finding (Task 9) + final whole-phase review I3
            // scope note: at 1280/1366px this table's natural (auto) column
            // widths add up to more than the periods column's share of the
            // two-column layout, and a `<table>` never shrinks below that —
            // without this wrapper the overflow pushed the WHOLE PAGE
            // horizontally, cutting off Estado/Pagos. `overflow-x: auto`
            // here contains that scroll to the table itself (brief's
            // explicitly allowed option), so the page never gains a
            // horizontal scrollbar at any width; below 768px the table
            // switches to stacked cards (member.css) and never needs it.
            <div className="gym-admin-detail-periods-table-wrapper">
              <table className="gym-table gym-periods-table gym-admin-detail-periods-table">
                <caption className="gym-sr-only">Historial de períodos</caption>
                <thead>
                  <tr>
                    <th scope="col">Plan</th>
                    <th scope="col">Período</th>
                    <th scope="col">Importe</th>
                    <th scope="col">Pagado</th>
                    <th scope="col">Saldo</th>
                    <th scope="col">Estado</th>
                    <th scope="col">Pagos</th>
                    <th scope="col">Cobro</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((membership) => {
                    const isRelevant = membership.id === detail.relevantMembershipId;
                    const isVoided = membership.status === 'voided';
                    return (
                      <tr key={membership.id} className={isRelevant ? 'gym-admin-detail-period--relevant' : (isVoided ? 'gym-admin-detail-period--voided' : undefined)}>
                        <td data-label="Plan">
                          {membership.planName}
                          {isRelevant && (
                            <span className="gym-badge gym-badge--info gym-admin-detail-period__tag">Período relevante</span>
                          )}
                        </td>
                        <td data-label="Período">{formatDate(membership.startDate)} – {formatDate(membership.endDate)}</td>
                        <td data-label="Importe">{formatMoney(membership.amount)}</td>
                        <td data-label="Pagado">{formatMoney(membership.paid)}</td>
                        <td data-label="Saldo">{formatMoney(membership.balance)}</td>
                        <td data-label="Estado"><StatusBadge status={membership.status} /></td>
                        <td data-label="Pagos">
                          {membership.payments && membership.payments.length > 0 ? (
                            <ul className="gym-payment-list">
                              {membership.payments.map((payment) => (
                                <li key={payment.id}>
                                  {formatPaymentDate(payment)} · {PAYMENT_METHOD_LABELS[payment.method] || payment.method} · {formatMoney(payment.amount)} · {PAYMENT_STATUS_LABELS[payment.status] || payment.status}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="gym-muted-text">Sin pagos</span>
                          )}
                        </td>
                        <td data-label="Cobro">
                          {isVoided ? (
                            <span className="gym-admin-detail-period__void-reason">
                              {membership.voidReason ? `Motivo: ${membership.voidReason}` : 'Período anulado'}
                            </span>
                          ) : (
                            <>
                              {membership.balance > 0 || paymentStore.forMembership(membership.id) ? (
                                <PaymentForm
                                  api={api}
                                  membership={membership}
                                  actorId={user && user.id}
                                  storage={paymentStorage}
                                  onResolved={reload}
                                />
                              ) : (
                                <span className="gym-muted-text">Saldado</span>
                              )}
                              <MembershipActions api={api} membership={membership} plans={plans} onChanged={handlePeriodChanged} />
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}
    </section>
  );
}
